import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Search, RefreshCw, Shield, Users, CheckSquare, Square, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { formatCurrency } from '../../lib/utils';
import { useDevolucion, useCreateDevolucion, useUpdateDevolucion } from '../../hooks/useDevoluciones';
import { useClients } from '../../hooks/useClients';
import { useProducts } from '../../hooks/useProducts';
import { useDebounce } from '../../hooks/useDebounce';
import { z } from 'zod';

// Schema de validación
const devolucionDetalleSchema = z.object({
  productoId: z.number().optional().nullable(),
  productoNombre: z.string().min(1, 'El nombre del producto es requerido'),
  productoCodigo: z.string().optional().nullable(),
  cantidad: z.coerce.number().min(1, 'La cantidad debe ser al menos 1'),
  precioUnitario: z.coerce.number().min(0, 'El precio no puede ser negativo'),
  subtotal: z.coerce.number().min(0),
  motivoDetalle: z.string().optional().nullable(),
});

const devolucionSchema = z.object({
  clienteId: z.number().optional().nullable(),
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteDocumento: z.string().optional().nullable(),
  clienteTelefono: z.string().optional().nullable(),
  clienteEmail: z.string().email('Email inválido').optional().nullable().or(z.literal('')),
  motivo: z.string().min(2, 'El motivo es requerido'),
  observaciones: z.string().optional().nullable(),
  tipoDevolucion: z.enum(['devolucion', 'cambio', 'garantia']),
  metodoReembolso: z
    .enum(['efectivo', 'transferencia', 'credito', 'tarjeta'])
    .optional()
    .nullable()
    .or(z.literal('')),
  detalles: z.array(devolucionDetalleSchema).min(1, 'Agrega al menos un producto'),
});

type DevolucionFormData = z.infer<typeof devolucionSchema>;

export function DevolucionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: devolucion, isLoading: devolucionLoading } = useDevolucion(id ? parseInt(id) : 0);
  const { data: clientsData } = useClients({ limit: 1000 });
  const clients = clientsData?.data ?? [];
  const createDevolucion = useCreateDevolucion();
  const updateDevolucion = useUpdateDevolucion();

  // Estados para dropdown de clientes
  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [autocompletar, setAutocompletar] = useState(true);
  const clienteDropdownRef = useRef<HTMLDivElement>(null);

  const [searchProduct, setSearchProduct] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cantidad, setCantidad] = useState(1);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearchProduct = useDebounce(searchProduct, 300);

  const shouldSearchProducts =
    searchProduct.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchProduct);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearchProduct.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DevolucionFormData>({
    resolver: zodResolver(devolucionSchema),
    defaultValues: {
      clienteNombre: '',
      clienteDocumento: '',
      clienteTelefono: '',
      clienteEmail: '',
      motivo: '',
      observaciones: '',
      tipoDevolucion: 'devolucion',
      metodoReembolso: undefined,
      detalles: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'detalles',
  });

  const watchDetalles = watch('detalles');
  const watchTipoDevolucion = watch('tipoDevolucion');
  const subtotal = watchDetalles?.reduce((sum, d) => sum + (Number(d.subtotal) || 0), 0) || 0;

  useEffect(() => {
    if (watchTipoDevolucion === 'cambio') {
      setValue('metodoReembolso', undefined);
    }
  }, [watchTipoDevolucion, setValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clienteDropdownRef.current && !clienteDropdownRef.current.contains(event.target as Node)) {
        setShowClienteDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cargar datos en edición
  useEffect(() => {
    if (devolucion && isEditing) {
      setValue('clienteId', devolucion.clienteId || undefined);
      setValue('clienteNombre', devolucion.clienteNombre || '');
      setValue('clienteDocumento', devolucion.clienteDocumento || '');
      setValue('clienteTelefono', devolucion.clienteTelefono || '');
      setValue('clienteEmail', devolucion.clienteEmail || '');
      setValue('motivo', devolucion.motivo || '');
      setValue('observaciones', devolucion.observaciones || '');
      setValue('tipoDevolucion', devolucion.tipoDevolucion as any);
      setValue('metodoReembolso', devolucion.metodoReembolso as any || undefined);

      if (devolucion.detalles && devolucion.detalles.length > 0) {
        setValue('detalles', devolucion.detalles.map((d: any) => ({
          productoId: d.productoId ? Number(d.productoId) : undefined,
          productoNombre: d.productoNombre,
          productoCodigo: d.productoCodigo || '',
          cantidad: Number(d.cantidad) || 1,
          precioUnitario: Number(d.precioUnitario) || 0,
          subtotal: Number(d.subtotal) || 0,
          motivoDetalle: d.motivoDetalle || '',
        })));
      }

      if (devolucion.clienteNombre) {
        const cliente = clients.find((c: any) =>
          c.nombre === devolucion.clienteNombre
        );
        if (cliente) {
          setClienteSeleccionado(cliente);
          setSearchCliente(cliente.nombre);
        }
      }
    }
  }, [devolucion, isEditing, setValue, clients]);

  // Filtrar clientes
  const filteredClients = clients.filter((c: any) => {
    const searchLower = searchCliente.toLowerCase();
    const nombreMatch = c.nombre?.toLowerCase().includes(searchLower) || false;
    const telefonoMatch = c.telefono?.toLowerCase().includes(searchLower) || false;
    return nombreMatch || telefonoMatch;
  }) || [];

  // Seleccionar cliente
  const handleSelectClient = (client: any) => {
    setClienteSeleccionado(client);
    setSearchCliente(client.nombre);
    setShowClienteDropdown(false);
    setValue('clienteId', client.id);
    setValue('clienteNombre', client.nombre);
    setValue('clienteTelefono', client.telefono || '');
    setValue('clienteEmail', client.email || '');

    if (autocompletar) {
      setValue('clienteDocumento', client.cuil || client.documento || '');
    } else {
      setValue('clienteDocumento', '');
    }

    toast.success(`Cliente seleccionado: ${client.nombre}`);
  };

  // Toggle autocompletar
  const toggleAutocompletar = () => {
    const nuevoEstado = !autocompletar;
    setAutocompletar(nuevoEstado);

    if (nuevoEstado && clienteSeleccionado) {
      setValue('clienteTelefono', clienteSeleccionado.telefono || '');
      setValue('clienteEmail', clienteSeleccionado.email || '');
      setValue('clienteDocumento', clienteSeleccionado.cuil || clienteSeleccionado.documento || '');
    }
  };

  const isFieldDisabled = !!clienteSeleccionado && autocompletar;

  // Agregar producto
  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto primero');
      return;
    }

    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const precio = Number(selectedProduct.precio) || 0;
    const subtotal = cantidad * precio;

    append({
      productoId: selectedProduct.id,
      productoNombre: selectedProduct.nombre,
      productoCodigo: selectedProduct.codigoBarras || '',
      cantidad,
      precioUnitario: precio,
      subtotal,
      motivoDetalle: '',
    });

    setSelectedProduct(null);
    setSearchProduct('');
    setCantidad(1);
    setShowProductDropdown(false);
    toast.success('Producto agregado');
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchProduct('');
    setCantidad(1);
    setShowProductDropdown(false);
  };

  // Eliminar producto
  const handleRemoveProduct = (index: number) => {
    remove(index);
  };

  // Actualizar cantidad
  const handleCantidadChange = (index: number, newCantidad: number) => {
    if (newCantidad < 1) return;
    const current = watchDetalles[index];
    if (current) {
      const newSubtotal = newCantidad * Number(current.precioUnitario);
      setValue(`detalles.${index}.cantidad`, newCantidad);
      setValue(`detalles.${index}.subtotal`, newSubtotal);
    }
  };

  // Enviar formulario
  const onSubmit = async (data: DevolucionFormData) => {
    try {
      const payload = {
        clienteId: clienteSeleccionado?.id || data.clienteId || undefined,
        clienteNombre: clienteSeleccionado?.nombre || data.clienteNombre,
        clienteDocumento: clienteSeleccionado?.cuil || clienteSeleccionado?.documento || data.clienteDocumento || undefined,
        clienteTelefono: clienteSeleccionado?.telefono || data.clienteTelefono || undefined,
        clienteEmail: clienteSeleccionado?.email || data.clienteEmail || undefined,
        motivo: data.motivo,
        observaciones: data.observaciones || undefined,
        tipoDevolucion: data.tipoDevolucion,
        metodoReembolso: (data.tipoDevolucion === 'cambio' || !data.metodoReembolso)
          ? undefined
          : data.metodoReembolso,
        detalles: data.detalles.map(d => ({
          productoId: d.productoId || undefined,
          productoNombre: d.productoNombre,
          productoCodigo: d.productoCodigo || undefined,
          cantidad: Number(d.cantidad),
          precioUnitario: Number(d.precioUnitario),
          subtotal: Number(d.subtotal),
          motivoDetalle: d.motivoDetalle || data.motivo,
        })),
      };

      if (isEditing && id) {
        await updateDevolucion.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createDevolucion.mutateAsync(payload);
      }

      navigate('/devoluciones');
    } catch (error: any) {
      console.error(error);
      toast.error('Error al guardar la devolución');
    }
  };

  const onError = (errors: any) => {
    console.error('Errores de validación detallados:', errors);
    const primerErrorKey = Object.keys(errors)[0];
    const mensajeError = errors[primerErrorKey]?.message || 'Por favor completa los campos requeridos del formulario';
    toast.error(mensajeError);
  };

  if (devolucionLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando devolución...</div>
      </div>
    );
  }

  const tiposDevolucion = [
    { value: 'devolucion', label: 'Devolución', icon: RefreshCw, color: 'blue' },
    { value: 'cambio', label: 'Cambio', icon: RefreshCw, color: 'green' },
    { value: 'garantia', label: 'Garantía', icon: Shield, color: 'purple' },
  ];

  const metodosReembolso = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'transferencia', label: 'Transferencia bancaria' },
    { value: 'credito', label: 'Crédito en tienda' },
    { value: 'tarjeta', label: 'Tarjeta de crédito' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/devoluciones')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Devolución' : 'Nueva Devolución'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda */}
          <div className="lg:col-span-2 space-y-6">
            {/* Datos del cliente */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="text-blue-500" size={20} />
                Datos del Cliente
              </h2>

              {/* Buscador de cliente */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar Cliente Existente
                </label>
                <div className="relative" ref={clienteDropdownRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar cliente por nombre o teléfono..."
                    value={searchCliente}
                    onChange={(e) => {
                      setSearchCliente(e.target.value);
                      setShowClienteDropdown(true);
                      if (!e.target.value) {
                        setClienteSeleccionado(null);
                        setValue('clienteId', null);
                        setValue('clienteNombre', '');
                        setValue('clienteTelefono', '');
                        setValue('clienteEmail', '');
                        setValue('clienteDocumento', '');
                      }
                    }}
                    onFocus={() => setShowClienteDropdown(true)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchCliente && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchCliente('');
                        setClienteSeleccionado(null);
                        setValue('clienteId', null);
                        setValue('clienteNombre', '');
                        setValue('clienteTelefono', '');
                        setValue('clienteEmail', '');
                        setValue('clienteDocumento', '');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={18} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {showClienteDropdown && searchCliente && filteredClients.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map((client: any) => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          onClick={() => handleSelectClient(client)}
                        >
                          <div>
                            <p className="font-medium text-gray-800">{client.nombre}</p>
                            {client.telefono && (
                              <p className="text-xs text-gray-400">{client.telefono}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle de autocompletado */}
              {clienteSeleccionado && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <button
                    type="button"
                    onClick={toggleAutocompletar}
                    className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    {autocompletar ? (
                      <CheckSquare className="text-blue-600" size={18} />
                    ) : (
                      <Square className="text-blue-400" size={18} />
                    )}
                    {autocompletar ? 'Autocompletar datos activado' : 'Autocompletar datos desactivado'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                  <Input
                    {...register('clienteNombre')}
                    placeholder="Nombre del cliente"
                    error={errors.clienteNombre?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <Input
                    {...register('clienteTelefono')}
                    placeholder="+54 9 11 1234-5678"
                    error={errors.clienteTelefono?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input
                    {...register('clienteEmail')}
                    type="email"
                    placeholder="cliente@email.com"
                    error={errors.clienteEmail?.message}
                    disabled={isFieldDisabled}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {clienteSeleccionado?.email && autocompletar
                      ? 'Email cargado automáticamente'
                      : 'Ingresa el email para confirmaciones'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                  <Input
                    {...register('clienteDocumento')}
                    placeholder="DNI / CUIL"
                    error={errors.clienteDocumento?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
              </div>
            </Card>

            {/* Productos */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Productos a Devolver</h2>

              {/* Buscar producto */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]" ref={productDropdownRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Buscar producto..."
                    value={searchProduct}
                    onChange={(e) => {
                      setSearchProduct(e.target.value);
                      setShowProductDropdown(true);
                      if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="pl-10 pr-10"
                  />

                  {selectedProduct && (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Quitar producto seleccionado"
                    >
                      <X size={16} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {showProductDropdown && searchProduct && filteredProducts.length > 0 && (
                    <div
                      className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl"
                      style={{
                        maxHeight: '200px',
                        height: 'auto',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      {filteredProducts.map((product: any) => {
                        const truncateDescription = (text: string) => {
                          if (!text) return '';
                          if (text.length <= 60) return text;
                          return text.substring(0, 60) + '.....';
                        };

                        return (
                          <button
                            key={product.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                            onClick={() => {
                              setSelectedProduct(product);
                              setSearchProduct(product.nombre);
                              setShowProductDropdown(false);
                            }}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                              {product.descripcion && (
                                <p className="text-xs text-gray-400 truncate">
                                  {truncateDescription(product.descripcion)}
                                </p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600 flex-shrink-0">
                              {formatCurrency(product.precio)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {showProductDropdown && searchProduct && loadingProducts && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Buscando productos...
                    </div>
                  )}

                  {showProductDropdown && searchProduct && !loadingProducts && filteredProducts.length === 0 && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>

                <div className="w-24">
                  <Input
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(Number(e.target.value) || 0)}
                    min={1}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleAddProduct}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus size={18} />
                  Agregar
                </Button>
              </div>

              {/* Lista de productos */}
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{field.productoNombre}</p>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(field.precioUnitario)} x {field.cantidad}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20">
                        <Input
                          type="number"
                          value={field.cantidad}
                          onChange={(e) => handleCantidadChange(index, Number(e.target.value) || 0)}
                          min={1}
                          className="text-center"
                        />
                      </div>
                      <p className="font-semibold text-gray-800 min-w-[80px] text-right">
                        {formatCurrency(field.subtotal)}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                {fields.length === 0 && (
                  <p className="text-center text-gray-400 py-4">
                    No hay productos agregados
                  </p>
                )}
              </div>
            </Card>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6">
            {/* Tipo de devolución */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Tipo de Devolución</h2>
              <div className="grid grid-cols-3 gap-2">
                {tiposDevolucion.map((tipo) => (
                  <button
                    key={tipo.value}
                    type="button"
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${
                      watchTipoDevolucion === tipo.value
                        ? `border-${tipo.color}-500 bg-${tipo.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setValue('tipoDevolucion', tipo.value as any)}
                  >
                    <tipo.icon size={20} className={`mx-auto mb-1 text-${tipo.color}-500`} />
                    <span className="text-xs font-medium">{tipo.label}</span>
                  </button>
                ))}
              </div>
              {errors.tipoDevolucion && (
                <p className="text-sm text-red-600 mt-2">{errors.tipoDevolucion.message}</p>
              )}
            </Card>

            {/* Motivo y observaciones */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Detalles</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo *
                  </label>
                  <select
                    {...register('motivo')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar motivo...</option>
                    <option value="Producto defectuoso">Producto defectuoso</option>
                    <option value="Producto equivocado">Producto equivocado</option>
                    <option value="Producto dañado en envío">Producto dañado en envío</option>
                    <option value="Insatisfacción del cliente">Insatisfacción del cliente</option>
                    <option value="Otro">Otro</option>
                  </select>
                  {errors.motivo && (
                    <p className="text-sm text-red-600 mt-1">{errors.motivo.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    {...register('observaciones')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Observaciones adicionales..."
                  />
                </div>
              </div>
            </Card>

            {/* Método de reembolso */}
            {watchTipoDevolucion !== 'cambio' && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Método de Reembolso</h2>
                <select
                  {...register('metodoReembolso')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar método...</option>
                  {metodosReembolso.map((metodo) => (
                    <option key={metodo.value} value={metodo.value}>
                      {metodo.label}
                    </option>
                  ))}
                </select>
              </Card>
            )}

            {/* Resumen */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total a reembolsar</span>
                    <span className="text-blue-600">{formatCurrency(subtotal)}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Botones */}
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Save size={18} />
                {isEditing ? 'Actualizar' : 'Crear'} Devolución
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/devoluciones')}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}